import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, X, Loader2 } from 'lucide-react';
import { User, Role } from '../../types';
import { isInvestorRoleName } from '../../utils/investorRole';

interface UserFormProps {
  user?: User;
  roles: Role[];
  onSubmit: (user: Omit<User, 'id' | 'createdAt' | 'roleName' | 'lastLogin'>) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export const UserForm: React.FC<UserFormProps> = ({
  user,
  roles,
  onSubmit,
  onCancel,
  isSubmitting = false,
}) => {
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    password: '',
    phone: user?.phone || '',
    roleId: user?.roleId || '',
    status: user?.status || 'active' as 'active' | 'inactive',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [roleSearchTerm, setRoleSearchTerm] = useState('');
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const roleDropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        phone: user.phone || '',
        roleId: user.roleId,
        status: user.status,
      });
      const selectedRole = roles.find(r => r.id === user.roleId);
      if (selectedRole) {
        setRoleSearchTerm(selectedRole.name);
      }
    } else {
      // Al crear, resetear el estado
      setFormData(prev => ({ ...prev, status: 'active' }));
      setRoleSearchTerm('');
    }
  }, [user, roles]);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target as Node)) {
        setIsRoleDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Requisito relajado: solo longitud mínima (6 caracteres)
  const MIN_PASSWORD_LENGTH = 6;
  const isPasswordStrong = !user
    ? formData.password.length >= MIN_PASSWORD_LENGTH
    : !formData.password.trim() || formData.password.length >= MIN_PASSWORD_LENGTH;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else {
      // Validación de email más segura
      const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
      const emailLower = formData.email.toLowerCase().trim();
      
      if (!emailRegex.test(emailLower)) {
        newErrors.email = 'El formato del email no es válido';
      } else if (emailLower.length > 254) {
        newErrors.email = 'El email es demasiado largo (máximo 254 caracteres)';
      } else if (emailLower.includes('..')) {
        newErrors.email = 'El email no puede contener puntos consecutivos';
      } else if (emailLower.startsWith('.') || emailLower.endsWith('.')) {
        newErrors.email = 'El email no puede comenzar o terminar con un punto';
      } else if (!emailLower.includes('@')) {
        newErrors.email = 'El email debe contener un símbolo @';
      } else {
        const [localPart, domain] = emailLower.split('@');
        if (localPart.length > 64) {
          newErrors.email = 'La parte local del email es demasiado larga';
        } else if (!domain || domain.length === 0) {
          newErrors.email = 'El dominio del email es requerido';
        } else if (!domain.includes('.')) {
          newErrors.email = 'El dominio del email debe contener al menos un punto';
        } else if (domain.startsWith('.') || domain.endsWith('.')) {
          newErrors.email = 'El dominio no puede comenzar o terminar con un punto';
        }
      }
    }
    if (!formData.roleId) {
      newErrors.roleId = 'Debe seleccionar un rol';
    }
    // Validar password solo al crear (no al editar, a menos que se proporcione)
    if (!user && !formData.password.trim()) {
      newErrors.password = 'La contraseña es requerida';
    } else if (formData.password.trim() && formData.password.length < MIN_PASSWORD_LENGTH) {
      newErrors.password = `La contraseña debe tener al menos ${MIN_PASSWORD_LENGTH} caracteres`;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const submitData: any = {
      name: formData.name.trim(),
      email: formData.email.toLowerCase().trim(), // Normalizar email
      phone: formData.phone?.trim() || '',
      roleId: formData.roleId,
      // Al crear, siempre usar 'active' como estado por defecto
      status: user ? formData.status : 'active',
    };

    // Solo incluir password si se proporciona (crear o actualizar)
    if (formData.password.trim()) {
      submitData.password = formData.password;
    }

    onSubmit(submitData);
  };

  const selectedRole = roles.find(r => r.id === formData.roleId);
  // Los inversionistas solo se crean desde el módulo de Inversionistas; no mostrar rol Inversor al crear/editar aquí
  const rolesForSelect = roles.filter((r) => !isInvestorRoleName(r.name));
  const isEditingInversor = !!user && isInvestorRoleName(selectedRole?.name);

  // Filtrar roles según el término de búsqueda
  const filteredRoles = rolesForSelect.filter((role) =>
    role.name.toLowerCase().includes(roleSearchTerm.toLowerCase()) ||
    role.description?.toLowerCase().includes(roleSearchTerm.toLowerCase())
  );

  const handleRoleSelect = (roleId: string) => {
    const role = roles.find(r => r.id === roleId);
    handleChange('roleId', roleId);
    setRoleSearchTerm(role?.name || '');
    setIsRoleDropdownOpen(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Nombre */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nombre <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            className={`w-full px-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400 ${
              errors.name ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Ej: Juan Pérez"
          />
          {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            className={`w-full px-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400 ${
              errors.email ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Ej: juan.perez@email.com"
            disabled={!!user}
          />
          {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
        </div>

        {/* Password */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Contraseña {!user && <span className="text-red-500">*</span>}
            {user && <span className="text-gray-500 text-xs">(dejar vacío para no cambiar)</span>}
          </label>
          <input
            type="password"
            value={formData.password}
            onChange={(e) => handleChange('password', e.target.value)}
            className={`w-full px-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400 ${
              errors.password ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder={user ? "Dejar vacío para no cambiar" : "Mín. 6 caracteres"}
          />
          
          {/* Indicador mínimo de contraseña */}
          {formData.password && (
            <p className={`mt-1 text-xs ${formData.password.length >= MIN_PASSWORD_LENGTH ? 'text-green-600' : 'text-gray-500'}`}>
              {formData.password.length >= MIN_PASSWORD_LENGTH ? '✓' : '○'} Mínimo {MIN_PASSWORD_LENGTH} caracteres
            </p>
          )}
          
          {errors.password && <p className="mt-1 text-sm text-red-500">{errors.password}</p>}
        </div>

        {/* Teléfono */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Teléfono
          </label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400"
            placeholder="Ej: +52 555 123 4567"
          />
        </div>

        {/* Rol con Autocompletado (Inversor no se puede crear/asignar aquí; se gestiona en Inversionistas) */}
        <div className="relative z-50" ref={roleDropdownRef}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Rol <span className="text-red-500">*</span>
          </label>
          {isEditingInversor ? (
            <div className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-700">
              Inversor — gestionado desde el módulo de Inversionistas
            </div>
          ) : (
            <>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={roleSearchTerm}
                  onChange={(e) => {
                    setRoleSearchTerm(e.target.value);
                    setIsRoleDropdownOpen(true);
                    if (!e.target.value) {
                      handleChange('roleId', '');
                    }
                  }}
                  onFocus={() => setIsRoleDropdownOpen(true)}
                  placeholder="Buscar rol..."
                  className={`w-full pl-10 pr-10 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                    errors.roleId ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {formData.roleId && (
                  <button
                    type="button"
                    onClick={() => {
                      handleChange('roleId', '');
                      setRoleSearchTerm('');
                    }}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 z-10"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
                {!formData.roleId && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none z-10">
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  </div>
                )}
              </div>
              {isRoleDropdownOpen && (
                <div className="absolute z-[100] w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl max-h-60 overflow-auto">
                  {filteredRoles.length > 0 ? (
                    filteredRoles.map((role) => (
                      <div
                        key={role.id}
                        onClick={() => handleRoleSelect(role.id)}
                        className={`px-4 py-2.5 cursor-pointer hover:bg-primary/10 transition-colors border-b border-gray-100 last:border-b-0 ${
                          formData.roleId === role.id ? 'bg-primary/20' : ''
                        }`}
                      >
                        <div className="font-semibold text-gray-900">{role.name}</div>
                        {role.description && (
                          <div className="text-xs text-gray-500 mt-0.5">{role.description}</div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-sm text-gray-500 text-center">
                      No se encontraron roles
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          {errors.roleId && <p className="mt-1 text-sm text-red-500">{errors.roleId}</p>}
          {selectedRole && !isRoleDropdownOpen && !isEditingInversor && (
            <p className="mt-1 text-xs text-gray-500">{selectedRole.description}</p>
          )}
        </div>

        {/* Estado - Solo mostrar al editar */}
        {user && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estado <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
              className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="active">Activo</option>
              <option value="inactive">Inactivo</option>
            </select>
          </div>
        )}
      </div>

      {/* Resumen del Rol */}
      {selectedRole && (
        <div className="p-4 bg-primary/10 rounded-lg">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Permisos del Rol</h4>
          <div className="flex flex-wrap gap-2">
            {selectedRole.permissions.map((permission) => {
              const permissionLabels: Record<string, string> = {
                view_dashboard: 'Ver Dashboard',
                view_properties: 'Ver Propiedades',
                create_properties: 'Agregar propiedades',
                update_properties: 'Editar propiedades',
                delete_properties: 'Eliminar propiedades',
                manage_properties: 'Gestionar propiedades (legado)',
                view_units: 'Ver Unidades',
                create_units: 'Agregar unidades',
                update_units: 'Editar unidades',
                delete_units: 'Eliminar unidades',
                manage_units: 'Gestionar unidades (legado)',
                view_investors: 'Ver Inversionistas',
                create_investors: 'Agregar inversionistas',
                update_investors: 'Editar inversionistas',
                delete_investors: 'Eliminar inversionistas',
                manage_investors: 'Gestionar inversionistas (legado)',
                view_rentals: 'Ver Rentas',
                create_rentals: 'Agregar rentas / inquilinos',
                update_rentals: 'Editar rentas / inquilinos',
                delete_rentals: 'Eliminar rentas / inquilinos',
                manage_rentals: 'Gestionar rentas (legado)',
                renew_tenant_contract: 'Renovar contratos de inquilinos',
                view_prospects: 'Rentas: Prospectos',
                view_tenants: 'Rentas: Inquilinos',
                view_rent_payments: 'Rentas: Pagos',
                view_applications: 'Ver Solicitudes',
                create_applications: 'Crear solicitudes',
                update_applications: 'Editar solicitudes',
                delete_applications: 'Eliminar solicitudes',
                manage_applications: 'Gestionar solicitudes (legado)',
                view_users: 'Ver Usuarios',
                create_users: 'Agregar usuarios',
                update_users: 'Editar usuarios',
                delete_users: 'Eliminar usuarios',
                manage_users: 'Gestionar usuarios (legado)',
                create_roles: 'Crear roles',
                update_roles: 'Editar roles',
                delete_roles: 'Eliminar roles',
                view_statistics: 'Ver todas las estadísticas (legado)',
                view_statistics_dashboard: 'Estadísticas: Dashboard resumen',
                view_statistics_occupancy: 'Estadísticas: Ocupación',
                view_statistics_revenue: 'Estadísticas: Ingresos brutos',
                view_statistics_price_m2: 'Estadísticas: Valor m²',
                view_statistics_units: 'Estadísticas: Unidades',
                manage_settings: 'Gestionar Configuración',
                view_property_management: 'Ver Administración de inmuebles',
                create_property_management: 'Registrar mantenimientos',
                update_property_management: 'Editar mantenimientos',
                delete_property_management: 'Eliminar mantenimientos',
                manage_property_management: 'Gestionar administración de inmuebles (legado)',
                view_maintenance_reports: 'Ver Reportes de mantenimiento',
                create_maintenance_reports: 'Crear reportes de mantenimiento',
                update_maintenance_reports: 'Editar reportes de mantenimiento',
                delete_maintenance_reports: 'Eliminar reportes de mantenimiento',
                manage_maintenance_reports: 'Gestionar reportes de mantenimiento (legado)',
                view_fideicomiso: 'Ver Fideicomiso',
                view_investor_account: 'Cuenta de Inversionista',
                view_prices: 'Ver precios',
                view_tenant_portal: 'Ver Portal Inquilino',
                view_tenant_payments: 'Ver Pagos Inquilino',
                create_tenant_maintenance_report: 'Crear Reporte Mantenimiento',
                request_tenant_renewal: 'Solicitar Renovación',
              };
              return (
                <span
                  key={permission}
                  className="px-2 py-1 bg-white text-gray-700 rounded text-xs border border-gray-200"
                >
                  {permissionLabels[permission] || permission.replace(/_/g, ' ')}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Botones */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={!isPasswordStrong || isSubmitting}
          className={`px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 min-w-[140px] ${
            isPasswordStrong && !isSubmitting
              ? 'bg-accent text-white hover:bg-accent/90 cursor-pointer'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
          title={!isPasswordStrong ? `La contraseña debe tener al menos ${MIN_PASSWORD_LENGTH} caracteres` : ''}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              {user ? 'Guardando…' : 'Creando…'}
            </>
          ) : (
            user ? 'Guardar Cambios' : 'Crear Usuario'
          )}
        </button>
      </div>
    </form>
  );
};

